import { useEffect, useState } from "react";
import {
  BarChart3,
  CalendarClock,
  CircleHelp,
  Copy,
  KeyRound,
  Mail,
  MessageSquareOff,
  Network,
  ShieldCheck,
  Smartphone,
  Sparkles,
  UsersRound,
  WalletCards,
} from "lucide-react";
import {
  createEmailComposeUrl,
  getContactEmailLabel,
  NexusFooter,
  NexusPublicNav,
  useContactEmail,
} from "./PublicSiteComponents";
import "./LandingPage.css";

const salesFeatures = [
  {
    icon: WalletCards,
    title: "Administrative Ledgering",
    copy: "Keep internal books, operating accounts, member records, and balance movement organized in one controlled workspace.",
  },
  {
    icon: UsersRound,
    title: "Organization Hub",
    copy: "Centralize your member directory, notes, communication context, status, and access records for faster decisions.",
  },
  {
    icon: ShieldCheck,
    title: "Operational Governance",
    copy: "Assign staff permissions, protect sensitive views, and give each operator the exact access their role requires.",
  },
  {
    icon: BarChart3,
    title: "Business Intelligence",
    copy: "Turn operational activity into useful analytics across members, accounts, staff flow, revenue categories, and trends.",
  },
  {
    icon: Smartphone,
    title: "Mobile Member Experience",
    copy: "Give members an optional mobile layer for RSVPs, waitlists, schedule viewing, rewards, and community updates.",
  },
  {
    icon: CalendarClock,
    title: "Scheduling Command",
    copy: "Coordinate events, staff coverage, and member-facing schedules from one live administrative surface.",
  },
];

const planGuides = [
  {
    id: "basic",
    name: "Basic",
    summary:
      "A clean starting point for organizers who need core administrative records and a more professional operating baseline.",
    features: [
      "Centralized administrative records",
      "Core member directory",
      "Current workspace activity view",
      "Owner-led setup guidance",
      "Email support",
    ],
  },
  {
    id: "standard",
    name: "Standard",
    summary:
    "A stronger operating layer for clubs that want mobile member access, event coordination, and staff scheduling structure.",
    features: [
      "Nexus Mobile access",
      "Event scheduling and RSVP workflows",
      "Staff scheduling basics",
      "Promotional tracking and rewards foundation",
      "Guided sandbox walkthrough",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    summary:
      "A premium command center with a base subscription plus active-member usage billing, built for operators whose monthly volume changes.",
    features: [
      "Full business intelligence dashboard",
      "AI-assisted operations insights",
      "Rewards, tiers, and referral tools",
      "Automated monthly reports",
      "Usage tied to members who actually played that month",
      "Priority support",
    ],
  },
];

const salesMetrics = [
  ["Admin Books", "Internal ledgering and operating account visibility"],
  ["Governance", "Role-based permissions for staff and operators"],
  ["Analytics", "Performance intelligence for better planning"],
];

const SCHEDULE_CALL_URL = "https://calendly.com/nexusclubengine/30min";

const sandboxSteps = [
  {
    icon: KeyRound,
    title: "Guided Sandbox",
    copy: "Walk through a sample workspace with an owner and see how administrative ledgering, member records, permissions, and scheduling connect.",
  },
  {
    icon: CalendarClock,
    title: "Operator Scenario Review",
    copy: "Bring your current workflow and we will map where Nexus fits, what changes for your staff, and which rollout path is simplest.",
  },
  {
    icon: CircleHelp,
    title: "Tier Fit Conversation",
    copy: "The call is the best place to understand exactly what each tier includes, how Pro active-member billing works, and which capabilities your club actually needs.",
  },
];

const qualificationCards = [
  {
    icon: Sparkles,
    title: "Best fit",
    copy: "Organizers who want to professionalize operations, secure data, and replace messy spreadsheets with a real administrative system.",
  },
  {
    icon: MessageSquareOff,
    title: "Common trigger",
    copy: "Your team is coordinating too much through scattered text threads, disconnected lists, and manual follow-ups.",
  },
  {
    icon: ShieldCheck,
    title: "Built for",
    copy: "Sustained private membership communities that need structure, accountability, analytics, and staff visibility.",
  },
];

const faqs = [
  {
    question: "Who is Nexus Game Engine built for?",
    answer:
      "Nexus is built for owners, admins, and operators of private membership clubs who need a professional system for records, staff workflows, member visibility, analytics, and daily operational control.",
  },
  {
    question: "Who is this not for?",
    answer:
      "It is not intended for casual, one-off public event ticketing. Nexus is designed for sustained private communities with recurring operations and ongoing member relationships.",
  },
  {
    question: "Is my data secure?",
    answer:
      "Security is a core operating requirement. Nexus uses authenticated access, role-based permissions, and controlled staff views so sensitive administrative information is only available to the people who need it.",
  },
  {
    question: "How hard is it to onboard my staff?",
    answer:
      "The platform is designed around clear roles and focused workflows. Staff can be invited into the workspace, assigned permissions, and trained around the specific tools they will actually use.",
  },
  {
    question: "Do members have to download the app?",
    answer:
      "No. The mobile app is optional, but it is highly recommended for RSVPs, waitlists, schedule viewing, rewards, and a smoother member experience.",
  },
  {
    question: "Can Nexus replace our spreadsheets?",
    answer:
      "Yes. Nexus is strongest when an organization is ready to move key records, operating accounts, member notes, and activity tracking out of disconnected spreadsheets and into one source of truth.",
  },
  {
    question: "When should we buy it?",
    answer:
      "Buy when your club has enough recurring activity that manual coordination is slowing decisions, hiding useful analytics, or making staff accountability harder than it should be.",
  },
  {
    question: "What if I am unsure which tier fits?",
    answer:
      "Start with a sales conversation. We can walk through your operating model, expected member volume, staff structure, and mobile needs before recommending the best tier.",
  },
  {
    question: "How does Pro usage billing work?",
    answer:
      "Pro is designed as a lower base subscription plus usage for active members who actually played during the month. That keeps the premium tier closer to real club activity instead of charging the same all-in amount during slower months.",
  },
];

export default function SalesPage() {
  const [emailCopied, setEmailCopied] = useState(false);
  const { contactEmail, isContactEmailLoaded } = useContactEmail();
  const salesEmailUrl = createEmailComposeUrl({
    email: contactEmail,
    subject: "Nexus sales inquiry",
  });
  const emailButtonLabel = contactEmail
    ? contactEmail
    : isContactEmailLoaded
      ? getContactEmailLabel(contactEmail)
      : "Loading contact email...";

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, []);

  const copySupportEmail = async () => {
    if (!contactEmail) return;

    try {
      await navigator.clipboard.writeText(contactEmail);
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 1800);
    } catch {
      if (salesEmailUrl) window.location.href = salesEmailUrl;
    }
  };

  return (
    <main className="nexus-home">
      <NexusPublicNav />

      <section className="nexus-page-hero" aria-labelledby="sales-title">
        <div className="nexus-page-hero__copy">
          <p className="nexus-eyebrow">Sales</p>
          <h1 id="sales-title">Nexus Game Engine for private club operations</h1>
          <p>
            A bold B2B administrative platform for owners, admins, and
            operators who need cleaner records, tighter controls, and better
            visibility across a sustained membership organization, with Pro
            pricing that can scale around active monthly member activity.
          </p>
          <div className="nexus-hero__actions">
            <a
              className="nexus-button nexus-button--primary"
              href={SCHEDULE_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Schedule a Call
            </a>
            <button
              className="nexus-button nexus-button--ghost"
              type="button"
              onClick={copySupportEmail}
              disabled={!contactEmail}
              aria-live="polite"
            >
              {emailCopied ? (
                <Mail size={18} aria-hidden="true" />
              ) : (
                <Copy size={18} aria-hidden="true" />
              )}
              {emailCopied ? "Copied" : emailButtonLabel}
            </button>
            {salesEmailUrl && (
              <a
                className="nexus-icon-button"
                href={salesEmailUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Email ${contactEmail}`}
                title={`Email ${contactEmail}`}
              >
                <Mail size={18} aria-hidden="true" />
              </a>
            )}
          </div>
        </div>
        <div className="nexus-page-panel" aria-label="Sales control summary">
          <div className="nexus-console__bar">
            <span />
            <span />
            <span />
          </div>
          <div className="nexus-sales-metrics">
            {salesMetrics.map(([label, detail]) => (
              <div key={label}>
                <span>{label}</span>
                <strong>{detail}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nexus-section nexus-section--systems">
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Control surface</p>
          <h2>The operating system behind a professional club</h2>
        </div>
        <div className="nexus-systems">
          {salesFeatures.map(({ icon: Icon, title, copy }) => (
            <article key={title}>
              <Icon size={22} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="nexus-section nexus-intelligence">
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Built for</p>
          <h2>Built for private membership operators ready to level up</h2>
        </div>
        <div className="nexus-systems">
          {qualificationCards.map(({ icon: Icon, title, copy }) => (
            <article key={title}>
              <Icon size={22} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="nexus-section" aria-labelledby="sales-pricing-title">
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Tier guidance</p>
          <h2 id="sales-pricing-title">Understand the right fit in a live walkthrough</h2>
          <p>
            Basic and Standard are fixed subscription tiers. Pro adds advanced
            tools with a base subscription and a monthly usage component for
            members who actually played.
          </p>
        </div>
        <div className="nexus-pricing">
          {planGuides.map((tier) => (
            <article className="nexus-price" key={tier.id}>
              <div className="nexus-price__top">
                <h3>{tier.name}</h3>
              </div>
              <p>{tier.summary}</p>
              <ul>
                {tier.features.map((feature) => (
                  <li key={feature}>
                    <Network size={15} aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="nexus-section nexus-section--systems" aria-labelledby="sandbox-title">
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Sandbox environment</p>
          <h2 id="sandbox-title">Try the operating system before choosing a tier</h2>
        </div>
        <div className="nexus-systems">
          {sandboxSteps.map(({ icon: Icon, title, copy }) => (
            <article key={title}>
              <Icon size={22} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="nexus-section" aria-labelledby="sales-faq-title">
        <div className="nexus-section__heading">
          <p className="nexus-eyebrow">Questions</p>
          <h2 id="sales-faq-title">What operators ask before buying</h2>
        </div>
        <div className="nexus-accordion">
          {faqs.map(({ question, answer }) => (
            <details key={question}>
              <summary>
                <span>{question}</span>
                <CircleHelp size={18} aria-hidden="true" />
              </summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="nexus-section nexus-page-cta" aria-label="Schedule a sandbox walkthrough">
        <div>
          <KeyRound size={26} aria-hidden="true" />
          <p className="nexus-eyebrow">Sandbox walkthrough</p>
          <h2>See the control center before you commit</h2>
          <p>
            Schedule a call and we will walk through the administrative ledger,
            organization hub, permissions, analytics, mobile flow, and event
            scheduling in a sandbox environment.
          </p>
        </div>
        <a
          className="nexus-button nexus-button--primary"
          href={SCHEDULE_CALL_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Schedule a Call
        </a>
      </section>

      <NexusFooter />
    </main>
  );
}
